import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { resetFetchedChats, setChats } from "../../subService/subServiceChats";
import { GetUserChatsDataTypes } from "../../../../Schema/getUserChats.types";

export type GetThirdPartyChatsLoadAction = {
  type: string;
  payload: { id: number | string | null; subServiceId: number; isSilent?: boolean };
};

export type GetThirdPartyChats = {
  data: {
    data: GetUserChatsDataTypes[];
    message_count: number;
    message_limit: number;
    share_token: string;
    images_base_path: string;
  };
};

const GetThirdPartyChats = new ReduxFetchState<
  GetThirdPartyChats | null,
  { id: number | string | null; subServiceId: number; isSilent?: boolean },
  any
>("getThirdPartyChats");

export function* watchGetThirdPartyChats(action: GetThirdPartyChatsLoadAction) {
  try {
    const { id, subServiceId } = action.payload;
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const data: GetThirdPartyChats = yield axios({
      url: `/api/v1/ai/third-party/${id}/get-chats`,
      method: "GET",
      headers: {
        lang,
      },
    });

    yield put(resetFetchedChats());
    yield put(GetThirdPartyChats.actions.loadSuccess({ data: data.data }));
    yield put(
      setChats({
        subServiceId,
        messages: data.data.data,
        share_token: data.data.share_token,
        images_base_path: data.data.images_base_path,
      }),
    );
  } catch (e: any) {
    yield put(GetThirdPartyChats.actions.loadFailure(e?.response?.data));
  }
}

export function* getThirdPartyChatsSagas() {
  yield takeEvery(GetThirdPartyChats.actionTypes.load, watchGetThirdPartyChats);
}

export function useGetThirdPartyChats() {
  const { loading, data, ...getThirdPartyChatsState } = useAppSelector(
    (state) => state.getThirdPartyChats,
  );
  const dispatch = useAppDispatch();

  const dispatchGetThirdPartyChats = useCallback(
    (data: { id: number | string | null; subServiceId: number; isSilent?: boolean }) => {
      dispatch(GetThirdPartyChats.actions.load(data, data.isSilent));
    },
    [dispatch],
  );

  return {
    ...getThirdPartyChatsState,
    dispatchGetThirdPartyChats,
    getThirdPartyChatsLoading: loading,
    getThirdPartyChatsData: data,
  };
}

export const {
  actionTypes: getThirdPartyChatsActionsTypes,
  actions: getThirdPartyChatsActions,
  reducer: getThirdPartyChatsReducer,
} = GetThirdPartyChats;
